// ============================================================
// VCE — SmartComposer (ejecutor de la decisión)
// ============================================================
// El motor DECIDE (contentProfile + composeDecision); el render es
// solo la CONSECUENCIA (ratificado 13:36). SmartComposer ejecuta la
// decisión sobre el SmartCard Layout Engine (FROZEN — se consume,
// jamás se toca) y SmartMedia (presentación por tipo + capacidades).
//
// El motor NO conoce negocios: recibe items crudos + descriptores
// neutrales + config del tenant. Cero imports de negocio. (P1)
// ============================================================
import React, { useMemo, useState } from 'react';
import SmartCard from '../SmartCard';
import SmartMedia, { RATIO_CLASS } from './SmartMedia';
import { normalizeVisualItems } from './adapters/contentAdapter';
import { buildContentProfile } from './analysis/contentProfile';
import { composeDecision } from './decision/composeDecision';
import './visual-engine.css';
import type {
  MediaCapabilities,
  PresentationContext,
  TenantVisualConfig,
} from './types';

export interface SmartComposerProps {
  /** Media cruda del consumidor (media[] tipado o images[] legacy) */
  media?: Array<{ type?: 'image' | 'video'; url: string }>;
  images?: string[];
  /** Texto alternativo (a11y — obligatorio en el consumidor) */
  alt?: string;
  blocked?: boolean;
  /** Descriptores NEUTROS para la decisión (los mide el consumidor) */
  infoLength?: number;
  hasDescription?: boolean;
  state?: string;
  actionCount?: number;
  context?: PresentationContext;
  /** Configuración del tenant (único conocimiento de negocio) */
  config?: TenantVisualConfig;
  /** Regiones de información (ReactNode ya compuesto por el consumidor) */
  header?: React.ReactNode;
  body?: React.ReactNode;
  footer?: React.ReactNode;
  overlay?: React.ReactNode;
  className?: string;
  /** Evento de UI genérico — el motor propaga, el consumidor decide */
  onClick?: () => void;
}

export const SmartComposer: React.FC<SmartComposerProps> = ({
  media,
  images,
  alt = '',
  blocked = false,
  infoLength = 0,
  hasDescription = false,
  state,
  actionCount = 0,
  context = 'card',
  config,
  header,
  body,
  footer,
  overlay,
  className,
  onClick,
}) => {
  // 1. Normalizar contenido (adaptador — frontera de formatos)
  const items = useMemo(() => normalizeVisualItems(media, images), [media, images]);

  // 2. Analizar (perfil NEUTRO)
  const profile = useMemo(
    () => buildContentProfile(items, { infoLength, hasDescription, state, actionCount }, context),
    [items, infoLength, hasDescription, state, actionCount, context],
  );

  // 3. DECIDIR (función pura — el corazón del motor)
  const decision = useMemo(() => composeDecision(profile, config), [profile, config]);

  // 4. Renderizar la decisión (consecuencia) sobre el SmartCard FROZEN
  const ratioClass = RATIO_CLASS[decision.mediaRatio] ?? RATIO_CLASS['3/4'];

  // Las capacidades del tenant (config) tienen prioridad sobre la decisión:
  // el consumidor decide qué capacidades usa realmente. (P6)
  const capabilities: MediaCapabilities = {
    ...decision.capabilities,
    ...(config?.capabilities ?? {}),
  };

  // ── Consumo de infoTreatment (P3): la decisión del motor gobierna el
  //    tratamiento de la información. 'clamped' → line-clamp + expansión
  //    (labels del tenant vía config.texts — el motor es agnóstico). ──
  const [expanded, setExpanded] = useState(false);
  const clampLines = config?.infoTreatment?.clampLines ?? 3;
  const expandable = config?.infoTreatment?.expandable ?? true;
  const expandLabel = config?.texts?.expandLabel ?? 'Ver más';
  const collapseLabel = config?.texts?.collapseLabel ?? 'Ver menos';
  const clamped = decision.infoTreatment === 'clamped' && expandable && hasDescription;

  const renderedBody = clamped ? (
    <div>
      <div
        className={expanded ? undefined : 'vce-clamp'}
        style={expanded ? undefined : ({ '--vce-lines': clampLines } as React.CSSProperties)}
      >
        {body}
      </div>
      {hasDescription && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setExpanded(v => !v); }}
          className="mt-1 text-xs font-medium text-purple-600 hover:text-purple-700"
        >
          {expanded ? collapseLabel : expandLabel}
        </button>
      )}
    </div>
  ) : body;

  // ── Consumo de actionPriority (R6): 'prominent' garantiza footer anclado.
  //    El SmartCard FROZEN ya ancla el footer (footerAlign bottom + mt-auto),
  //    por lo que la decisión se traduce estructuralmente; el consumidor
  //    compone el contenido del footer (prominencia visual = su contenido). ──
  // (documentado: actionPriority no añade clases propias — el motor delega
  //  la prominencia de CONTENIDO al consumidor, la prominencia ESTRUCTURAL
  //  al SmartCard FROZEN.)

  return (
    <SmartCard
      variant={decision.variant}
      onClick={onClick}
      className={className}
      regions={{
        media: (
          <SmartMedia
            media={media}
            images={images}
            blocked={blocked}
            ratioClass={ratioClass}
            alt={alt}
            capabilities={capabilities}
            config={config}
          />
        ),
        overlay,
        header,
        body: renderedBody,
        footer,
      }}
    />
  );
};

export default SmartComposer;
